// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/** How one requirement moved between the two texts. */
export const REQUIREMENT_CHANGES = ['unchanged', 'changed', 'removed', 'new'];
/**
 * How a comparison's rows were obtained: `computed` from the two installed
 * versions of one framework code (AC-F1), or `declared` by a superseding
 * framework's `supersedes[].requirement_correspondence` (AC-F2).
 */
export const COMPARISON_BASES = ['computed', 'declared'];
/**
 * Why a `pack_upgrade` update names a framework that was installed before and
 * still holds no comparison (`RegulatoryUpdate.comparison_withheld`).
 *
 * `other_pack` — the framework record the upgrade replaced had been written by
 * another pack declaring the same code (or by a source that recorded no pack
 * at all). Its requirements are not an earlier version of the upgrading pack's
 * text, so a requirement-by-requirement comparison would report the
 * differences between two packs as a change of the text, and raise a review
 * task for it. The update is still recorded; no comparison is computed.
 */
export const COMPARISON_WITHHELD_REASONS = ['other_pack'];
/** An organisation's reading of one comparison row (AC-F4). */
export const COMPARISON_READINGS = ['affects_us', 'does_not_affect_us', 'to_assess'];
/**
 * The normative fields of a requirement: a `changed` row means the canonical
 * hash of these differs between the two texts. Order is irrelevant (the hash
 * sorts keys); listed once so the backend and a test cannot disagree.
 */
export const REQUIREMENT_NORMATIVE_FIELDS = [
    'title',
    'description',
    'mandatory',
    'status',
    'reference',
    'addressed_to',
    'addressee_class_ids',
    'applicable_module',
];
/**
 * Gate E12.5 pass 1, P2-3 — where the reviews live. They used to be appended
 * to the update document (`comparison_reviews[]`), which grew without bound
 * and was read whole by every comparison read; each review is now its own
 * row, org-scoped and append-only (insert only: no update, no delete).
 */
export const REGULATORY_UPDATE_COMPARISON_REVIEWS_COLLECTION = 'grc_regulatory_update_comparison_reviews';
/**
 * Gate E12.5 pass 2, Minor-1 — whether a regulatory update has a comparison to
 * open: one is stored (`framework_comparisons`, a same-code upgrade since Slice
 * F), or it is a supersession (`framework_superseded`), whose rows the
 * comparison read derives from the installed texts when it stores none. A
 * withheld comparison, a `pack_upgrade` written before Slice F (or by a pack
 * that declares no framework) and an update recorded by hand have none. The
 * list read stamps it on each row (`RegulatoryUpdate.has_comparison`), so the
 * register links a comparison only where there is one.
 */
export function regulatoryUpdateHasComparison(update) {
    if (update.comparison_withheld)
        return false;
    if (Array.isArray(update.framework_comparisons) && update.framework_comparisons.length > 0) {
        return true;
    }
    // A supersession storing no rows has one only while the read can derive it;
    // the list read asks that of the installed texts (gate E12.5 pass 3, P3-2:
    // `derivableSupersessionIds`), and this answer is its upper bound.
    return update.update_type === 'framework_superseded';
}
/**
 * Why the installer did not raise the review task for an update it wrote
 * (`RegulatoryUpdate.review_task`). `nothing_changed`: every row reads
 * `unchanged`; `duties_not_owed`: the *to* framework owes no duties;
 * `no_comparison`: the record holds no stored comparison (withheld, or a
 * record whose rows are derived on read); `no_assignee`: no active user
 * holds `regulatory_framework:write`, so the task would have had no one to go
 * to (gate E12.5 pass 1, P3-7 — logged, never raised unassigned);
 * `not_operational`: the *to* framework does not operate in the organisation —
 * a sector overlay installed for reference, or one the installation's package
 * does not entitle (`OptionalOverlayPolicy`, gate E12.5 pass 2, P3-D), or a
 * framework record whose status retired it (`superseded` or `legacy`; gate
 * E12.5 step-2 verification, P3-3).
 */
export const COMPARISON_REVIEW_TASK_NOT_RAISED_REASONS = [
    'nothing_changed',
    'duties_not_owed',
    'no_comparison',
    'no_assignee',
    'not_operational',
];
//# sourceMappingURL=regulatory-update-comparison.js.map